import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Container, AppBar, Toolbar, Typography, Button, Avatar, Menu, MenuItem, Chip, IconButton, Tooltip, Alert, Badge, Drawer, List, ListItemButton, ListItemIcon, ListItemText, useMediaQuery, useTheme, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout, setThemeMode } from '../../store/authSlice';
import { AppDispatch, RootState } from '../../store';
import api from '../../services/api';
import CommandPalette from './CommandPalette';
import HotkeyOverlay from './HotkeyOverlay';
import ManualScanReminderBanner from './ManualScanReminderBanner';
import RouteTransition from './RouteTransition';
import LastSync from './LastSync';
import { useAccountStats, refreshAccountStats, openPnlExplain } from '../../stores/accountStatsStore';
import { formatSignedCurrency, formatCurrency } from '../../utils/formatters';
import TabStrip from './TabStrip';
import { useTabs } from '../../contexts/TabsContext';
import { useHotkeySequence } from '../../hooks/useHotkeys';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupsIcon from '@mui/icons-material/Groups';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import NotificationsIcon from '@mui/icons-material/Notifications';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ScienceIcon from '@mui/icons-material/Science';
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import InsightsIcon from '@mui/icons-material/Insights';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BugReportIcon from '@mui/icons-material/BugReport';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

const Layout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { tabs } = useTabs();
  const location = useLocation();
  const muiTheme = useTheme();
  /** "Compact" = horizontal nav too narrow to fit all chips comfortably.
   *  Below 900px we collapse to a hamburger drawer. */
  const isCompact = useMediaQuery(muiTheme.breakpoints.down('md'));
  /* Width tiers for the nav-priority split (see menuItems below). */
  const navWide = useMediaQuery('(min-width:1500px)');
  const navXWide = useMediaQuery('(min-width:1750px)');
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { user, themeMode } = useSelector((state: RootState) => state.auth);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [zerodhaWarning, setZerodhaWarning] = useState(false);
  // R115 Stage 9 — live IST clock in the top breadcrumb.
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const [zerodhaWarnDismissed, setZerodhaWarnDismissed] = useState(false);
  // R117 — admin probe so we can conditionally render the E2E Tests nav
  // entry. Same pattern UserSettings uses: try /admin/config, succeed →
  // admin. Suppress the 403 toast so non-admins don't see noise.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    api.get('/admin/config', { headers: { _suppressToast: 'true' } })
      .then((r: any) => { if (r?.data?.success) setIsAdmin(true); })
      .catch(() => { /* not admin — leave false */ });
  }, []);

  // Account figures for the header pill and the freshness stamp. Both come from the
  // shared `accountStatsStore`, which is ref-counted: the Charts title rail subscribes to
  // the same store, so two surfaces cost one set of polls rather than two.
  //
  // What used to be here: a 60s `loadHeavy` that round-tripped /broker/zerodha/status and
  // /broker/delta/status to populate `stats.zerodha`/`stats.delta`/`stats.health` -- state
  // that nothing in this file ever read, because the connection dots moved to
  // MarketStatusRail. Those two requests per minute are gone.
  const acct = useAccountStats();
  const stats = { recentAlerts: acct.recentAlerts, openPnl: acct.openPnl };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleTheme = () => {
    dispatch(setThemeMode(themeMode === 'dark' ? 'light' : 'dark'));
  };

  const isActive = (path: string) => {
    if (path === '/options-dashboard') {
      return location.pathname === '/options-dashboard' ||
        location.pathname.startsWith('/options-lab') ||
        location.pathname === '/app-guide';
    }
    // Strategies, Options AT and Golden Signals live as three tabs under one
    // hub — the single "Strategies" nav entry highlights for all of them.
    if (path === '/strategies') {
      return location.pathname === '/strategies'
        || location.pathname === '/option-strategies'
        || location.pathname === '/golden-signals';
    }
    /* Segment-prefix match, not equality. `/charts/BTCUSD`, `/trades/123` and
       every other nested route left its nav item unhighlighted, so on a symbol
       chart — the page people spend the most time on — nothing in the header
       told you where you were.
       The `+ '/'` matters: a bare startsWith would make `/options` claim
       `/options-dashboard`, which is a different page with its own entry. */
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // ── Unread Alerts badge ───────────────────────────────────────────────────
  // `stats.recentAlerts` counts alerts in the current period; we mark them as
  // "seen" the moment the user visits /alerts. Unread = recent − lastSeen.
  const LAST_SEEN_KEY = 'alerts.lastSeenCount.v1';
  const [lastSeenAlerts, setLastSeenAlerts] = useState<number>(() => {
    try { return parseInt(localStorage.getItem(LAST_SEEN_KEY) || '0', 10) || 0; } catch { return 0; }
  });
  useEffect(() => {
    if (location.pathname === '/alerts') {
      try { localStorage.setItem(LAST_SEEN_KEY, String(stats.recentAlerts)); } catch { /* ignore */ }
      setLastSeenAlerts(stats.recentAlerts);
    }
  }, [location.pathname, stats.recentAlerts]);
  const unreadAlerts = Math.max(0, stats.recentAlerts - lastSeenAlerts);

  // ── Global navigation hotkeys (g-prefix) ──────────────────────────────────
  useHotkeySequence('g', {
    d: () => navigate('/dashboard'),
    t: () => navigate('/trades'),
    n: () => navigate('/analytics'),
    a: () => navigate('/alerts'),
    s: () => navigate('/strategies'),
    c: () => navigate('/charts'),
    f: () => navigate('/options-dashboard'),
    i: () => navigate('/instruments'),
    l: () => navigate('/logs'),
    h: () => window.open('/app-guide', '_blank', 'noopener,noreferrer'),
    ',': () => navigate('/settings'),
  }, { preventDefault: true });

  // The 30s `setInterval(() => setLastUpdated(new Date()), 30000)` that used to sit here
  // FETCHED NOTHING. It advanced the "Updated Ns ago" stamp on a timer, so the badge could
  // never read older than 30s even with the backend down, and the 10s open-P&L poll -- the
  // freshest number on the bar -- never touched it. The stamp now comes from
  // `acct.lastSuccessAt`, which only moves when a response actually lands.

  // Poll Zerodha session status every 5 minutes. The banner is a DAILY-LOGIN
  // reminder: the Kite token dies ~06:00 IST, and without a fresh login the
  // NSE/MCX option-data capture + SL/Target monitoring sit idle all day (even
  // when no trade is planned). It shows from 08:00 IST on NSE WORKING days and
  // goes away ONLY when the user logs in to Zerodha or dismisses it (per-day).
  const [nseWorkReason, setNseWorkReason] = useState<string>('');
  useEffect(() => {
    const checkZerodhaStatus = async () => {
      try {
        const resp = await api.get<{ hasCredentials: boolean; connected: boolean }>('/broker/zerodha/status');
        if (resp.data.hasCredentials && !resp.data.connected) {
          setZerodhaWarning(true);
        } else {
          setZerodhaWarning(false);
          setZerodhaWarnDismissed(false); // re-show if they log in then expire again
        }
        // NSE working-day awareness (weekends + exchange holidays) from the server calendar.
        const ms: any = await api.get('/options/market-status', { headers: { _suppressToast: 'true' } }).catch(() => null);
        const nse = (ms?.data?.data?.exchanges || []).find((s: any) => s.exchange === 'NSE');
        if (nse) setNseWorkReason(String(nse.reason || ''));
      } catch {
        // ignore status check errors
      }
    };
    checkZerodhaStatus();
    const interval = setInterval(checkZerodhaStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  const istNowStr = now.toLocaleString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false });
  const istHour = Number(now.toLocaleString('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', hour12: false }));
  const istToday = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  void istNowStr;
  const isNseWorkingDay = nseWorkReason !== 'WEEKEND' && nseWorkReason !== 'HOLIDAY' && nseWorkReason !== '';
  const [zBannerDismissedFor, setZBannerDismissedFor] = useState<string>(() => {
    try { return localStorage.getItem('zerodhaLoginBannerDismissed_v1') || ''; } catch { return ''; }
  });
  const dismissZerodhaBanner = () => {
    setZBannerDismissedFor(istToday);
    try { localStorage.setItem('zerodhaLoginBannerDismissed_v1', istToday); } catch { /* ignore */ }
  };
  const connectZerodhaNow = async () => {
    try {
      const r: any = await api.get('/broker/zerodha/initiate-login');
      const url = r?.data?.data?.loginUrl;
      if (url) window.open(url, '_blank', 'noopener');
      else navigate('/settings');
    } catch { navigate('/settings'); }
  };
  const showZerodhaLoginBanner = zerodhaWarning && isNseWorkingDay && istHour >= 8 && istHour < 16 && zBannerDismissedFor !== istToday;

  // R115 Stage 9 — page nav order finalised:
  //   Dashboard | Charts | Trades | Alerts | Strategies | BackTest | FNO |
  //   Analytics | Instruments | Logs | Settings | Guide
  // "App Guide" renamed to just "Guide".
  /* NAV PRIORITY (2026-09-02)
   * The row used to render all 17 items with `flexShrink: 0`, so when they no
   * longer fit nothing yielded and the groups painted over one another — the
   * comments further down record the resulting collisions ("Prashant" over
   * "Dashboard", labels reading "Dash18ard"). The mitigation was to hide the
   * status cluster at hard breakpoints, which silently removed information on
   * any laptop-width screen.
   *
   * Instead each item now declares how much room it deserves, and whatever does
   * not fit at the current width moves into "More" — reachable at every width,
   * never overlapping, nothing silently dropped.
   *   1 = always on the bar
   *   2 = on the bar at >= 1500px
   *   3 = on the bar at >= 1750px
   *   4 = always in More (utilities)                                        */
  const menuItems = [
    { label: 'Dashboard',   path: '/dashboard',          icon: <DashboardIcon />,   pri: 1 },
    { label: 'Charts',      path: '/charts',             icon: <CandlestickChartIcon />, pri: 1 },
    { label: 'Watchlist',   path: '/watchlist',          icon: <VisibilityIcon />,  pri: 2 },
    { label: 'Trades',      path: '/trades',             icon: <TrendingUpIcon />,  pri: 1 },
    // Analytics = the post-trade REVIEW companion to Trades, so it sits next to it
    // (rather than stranded in the utility cluster). AI Desk / Smart Money below are
    // the forward-looking SIGNAL generators — a deliberately separate job.
    //
    // Edge AI used to sit beside AI Desk here. Two AI entries was one too many and the reader had
    // no way to tell which to open; the parts of it worth keeping are now the AI Desk's Engine,
    // Options and Performance tabs, and /edge-ai redirects there.
    { label: 'Analytics',   path: '/analytics',          icon: <InsightsIcon />, pri: 2 },
    { label: 'Options',     path: '/options',            icon: <AccountTreeIcon />, pri: 1 },
    { label: 'Alerts',      path: '/alerts',             icon: <NotificationsIcon />, pri: 1 },
    { label: 'Strategies',  path: '/strategies',         icon: <ShowChartIcon />, pri: 1 },
    { label: 'AI Desk',     path: '/ai-desk',            icon: <PsychologyIcon />, pri: 3 },
    { label: 'Smart Money', path: '/smart-money',        icon: <GroupsIcon />, pri: 3 },
    { label: 'Backtest',    path: '/backtest',           icon: <ScienceIcon />, pri: 3 },
    { label: 'Instruments', path: '/instruments',        icon: <ShowChartIcon />, pri: 4 },
    { label: 'Logs',        path: '/logs',               icon: <ArticleIcon />, pri: 4 },
    { label: 'Settings',    path: '/settings',           icon: <SettingsIcon />, pri: 2 },
    // R115 — "App Guide" → "Guide". Still opens in a new tab.
    { label: 'Guide',       path: '/app-guide',          icon: <MenuBookIcon />, newTab: true, pri: 4 },
    // R117 — admin-only end-to-end trade-cycle test harness.
    ...(isAdmin ? [{ label: 'E2E Tests', path: '/e2e-tests', icon: <BugReportIcon />, pri: 4 }] : []),
  ];

  /* Split the nav by priority against the current width. Anything not on the
     bar goes to More, so the set of reachable pages never changes. */
  const onBar = (pri?: number) => pri === 1 || (pri === 2 && navWide) || (pri === 3 && navXWide);
  const barItems = menuItems.filter(i => onBar((i as any).pri));
  const overflowItems = menuItems.filter(i => !onBar((i as any).pri));

  return (
    <Box
      // --tabstrip-h: height the sticky tab strip occupies below the AppBar (0
      // when only the Main tab is open). Sticky page headers (e.g. the Dashboard
      // Risk Cockpit) offset by this so they freeze below the tab strip, not under it.
      style={{ ['--tabstrip-h' as any]: tabs.length > 1 ? '38px' : '0px' }}
      sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflowY: 'auto', backgroundColor: 'background.default' }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          zIndex: 1100,
          // Deeper, more even-toned navy with subtle inner highlight on top — feels less
          // hand-painted than the high-contrast 135° gradient and reads as a polished bar.
          background: 'linear-gradient(180deg, #102347 0%, #0b1c3b 60%, #091933 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.05) inset, 0 2px 8px rgba(0,0,0,0.35)',
          '@keyframes pulseRed': {
            '0%, 100%': { boxShadow: '0 0 0 0 rgba(244,67,54,0.7)' },
            '50%': { boxShadow: '0 0 0 6px rgba(244,67,54,0)' },
          },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 48, sm: 52 }, py: 0.25, gap: 1.5 }}>
          {/* Zone 1 — brand + live status. `minWidth: 0` plus a shrinkable
              status cluster is what stops this zone from painting over the nav
              when the window narrows; previously every child set flexShrink: 0
              and the two zones simply overlapped. */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0, gap: 1.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 800,
                fontSize: '1.1rem',
                letterSpacing: '0.4px',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: 0.7,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                /* The nav used to paint over the brand at ~1366px. The row can
                   now yield (the nav overflows into "More"), and the brand keeps
                   a hard floor so it is never the thing that gets crushed. */
                flexShrink: 0,
                minWidth: 'fit-content',
                mr: 0.5,
              }}
            >
              <TrendingUpIcon sx={{ color: '#7eb6ff', fontSize: 22, flexShrink: 0 }} />
              Prashant
            </Typography>
            {/* R115 follow-up — clock relocated to the leftmost end so it isn't
                hidden when the nav row wraps onto the chip group. Compact
                styling (no padding/border) so the chip group + Open P&L stay
                visible within the toolbar width. */}
            {/* Clock + last-sync: hidden below 1500px.
                Same cause as the pills below — every group in this toolbar sets flexShrink: 0, so
                when the total exceeds the available width nothing yields and the groups paint over
                each other. Between 1280 and 1440 this collided with the nav ("Prashant" over
                "Dashboard", the clock over "Charts"). The clock duplicates the OS clock and the
                sync age is a nicety, so they are the right things to drop first: hiding them keeps
                the full nav readable down to ~1150px, rather than forcing the hamburger onto
                every laptop-width screen. */}
            <Tooltip title="Current system time (IST)" arrow>
              <Box sx={{
                fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8rem',
                color: '#cfe2ff', letterSpacing: 0.4,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0,
                display: 'none', '@media (min-width:1400px)': { display: 'block' },
              }}>
                {now.toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
              </Box>
            </Tooltip>
            <LastSync
              lastSyncAt={acct.lastSuccessAt}
              error={acct.failing}
              onRetry={refreshAccountStats}
              prefix="Updated"
              sx={{
                display: 'none', '@media (min-width:1400px)': { display: 'flex' },
                '& .MuiTypography-root': { color: acct.failing ? '#ef9a9a' : 'rgba(255,255,255,0.6)', fontSize: '0.68rem', fontWeight: 500, letterSpacing: 0.2 },
              }}
            />
            {/* Hairline divider. NOTE `width: '1px'`, not `width: 1` — in MUI's
                sx a unitless 0–1 width is a PERCENTAGE, so `width: 1` rendered a
                100%-wide grey block that flex then shrank to ~30px. Two of these
                were showing in the header as unexplained empty grey boxes. */}
            <Box sx={{
              width: '1px', height: 18, bgcolor: 'rgba(255,255,255,0.15)', flexShrink: 0,
              display: 'none', '@media (min-width:1280px)': { display: 'block' },
            }} />
            {/* Broker/App connection dots moved to the Dashboard left rail
                (MarketStatusRail → "Connections") to reclaim breadcrumb space. */}
            {/* Hidden below 1800px.
                This group and the nav row both set flexShrink: 0, so once the combined width
                exceeds the toolbar there is no give and they simply paint over each other. At
                1600px that produced four measurable text collisions — "Alerts" over "Dashboard",
                "Open P&L" over "Charts", the P&L figure over "Watchlist" — which read as garbled
                labels like "Dash18ard". Neither figure is lost by hiding it: the unread-alert
                count is on the Alerts nav item, and Open P&L is on the Dashboard in both the Risk
                Cockpit and Trading Controls. 1800px is where the row measures clean. */}
            {/* Live-status cluster. The Alerts item that used to sit here was
                removed: the Alerts NAV item already carries the unread badge, and
                the two showed DIFFERENT figures (unread-since-last-visit vs total
                in the period) side by side under one word, which the code comment
                below the old block itself called "actively confusing".
                Open P&L stays — it is the one figure the header shows that is not
                reachable from anywhere else in the chrome. It now appears from
                1280px rather than 1800px: with the nav priority-split there is
                room for it on a laptop, and hiding a live P&L was never the right
                thing to drop. */}
            <Box sx={{
              display: 'none', '@media (min-width:1280px)': { display: 'flex' },
              alignItems: 'center', gap: 1.4, fontSize: '0.72rem', fontWeight: 600,
              color: 'rgba(255,255,255,0.9)', flexWrap: 'nowrap', minWidth: 0,
            }}>
              <Tooltip title={openPnlExplain(acct, v => formatCurrency(v, 'INR', 2))} arrow>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 0.9, py: 0.25, borderRadius: 1,
                  bgcolor: stats.openPnl == null
                    ? 'rgba(255,255,255,0.06)'
                    : stats.openPnl >= 0 ? 'rgba(76,175,80,0.16)' : 'rgba(244,67,54,0.16)',
                  border: '1px solid',
                  borderColor: stats.openPnl == null
                    ? 'rgba(255,255,255,0.08)'
                    : stats.openPnl >= 0 ? 'rgba(76,175,80,0.35)' : 'rgba(244,67,54,0.35)',
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.66rem' }}>Open P&amp;L</span>
                  <b style={{
                    color: stats.openPnl == null ? '#cfd8dc' : stats.openPnl >= 0 ? '#a5d6a7' : '#ef9a9a',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {formatSignedCurrency(stats.openPnl, 'INR', 0)}{acct.basketsUnpriced > 0 ? '*' : ''}
                  </b>
                </Box>
              </Tooltip>
            </Box>
          </Box>

          {/* Navigation Menu — full pills at ≥md, hamburger drawer below */}
          {isCompact ? (
            <Tooltip title="Menu">
              <IconButton
                onClick={() => setMobileNavOpen(true)}
                size="small"
                sx={{ color: '#ffffff', mr: 0.5 }}
              >
                <Badge color="error" badgeContent={unreadAlerts} invisible={unreadAlerts === 0}>
                  <MenuIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1, mr: 0.5, flexShrink: 0 }}>
              {barItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  startIcon={
                    item.path === '/alerts' && unreadAlerts > 0 ? (
                      <Badge
                        color="error"
                        badgeContent={unreadAlerts}
                        sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', minWidth: 14, height: 14, padding: '0 3px', top: 2, right: -2 } }}
                      >
                        {item.icon}
                      </Badge>
                    ) : item.icon
                  }
                  onClick={() => {
                    if ((item as any).newTab) {
                      window.open(item.path, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate(item.path);
                    }
                  }}
                  sx={{
                    // R115 follow-up — slimmer nav buttons so all 12 items
                    // plus the left-side status group + Open P&L pill all
                    // fit on the breadcrumb without clipping.
                    px: 0.9,
                    py: 0.5,
                    minWidth: 0,
                    borderRadius: 1,
                    fontWeight: isActive(item.path) ? 700 : 500,
                    fontSize: '0.74rem',
                    textTransform: 'none',
                    letterSpacing: 0.1,
                    whiteSpace: 'nowrap',
                    color: isActive(item.path) ? '#ffffff' : 'rgba(255,255,255,0.78)',
                    backgroundColor: isActive(item.path) ? 'rgba(255,255,255,0.12)' : 'transparent',
                    borderBottom: isActive(item.path) ? '2px solid #7eb6ff' : '2px solid transparent',
                    '& .MuiButton-startIcon': { mr: 0.4, '& > *:first-of-type': { fontSize: 14 } },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      color: '#ffffff',
                    },
                    transition: 'all 0.15s ease',
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {/* Overflow — everything that does not fit at this width, plus the
                  utilities that never belong on the bar. Nothing is unreachable. */}
              {overflowItems.length > 0 && (
                <>
                  <Tooltip title="More pages" arrow>
                    <Button
                      color="inherit"
                      onClick={(e) => setMoreAnchor(e.currentTarget)}
                      endIcon={<ArrowDropDownIcon />}
                      sx={{
                        px: 0.9, py: 0.5, minWidth: 0, borderRadius: 1, fontWeight: 500,
                        fontSize: '0.74rem', textTransform: 'none', letterSpacing: 0.1,
                        whiteSpace: 'nowrap',
                        color: overflowItems.some(i => isActive(i.path)) ? '#ffffff' : 'rgba(255,255,255,0.78)',
                        backgroundColor: overflowItems.some(i => isActive(i.path)) ? 'rgba(255,255,255,0.12)' : 'transparent',
                        borderBottom: overflowItems.some(i => isActive(i.path)) ? '2px solid #7eb6ff' : '2px solid transparent',
                        '& .MuiButton-endIcon': { ml: 0.1, '& > *:first-of-type': { fontSize: 18 } },
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: '#ffffff' },
                      }}
                    >
                      More
                    </Button>
                  </Tooltip>
                  <Menu
                    anchorEl={moreAnchor}
                    open={!!moreAnchor}
                    onClose={() => setMoreAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {overflowItems.map(item => (
                      <MenuItem
                        key={item.path}
                        selected={isActive(item.path)}
                        onClick={() => {
                          setMoreAnchor(null);
                          if ((item as any).newTab) window.open(item.path, '_blank', 'noopener,noreferrer');
                          else navigate(item.path);
                        }}
                        sx={{ fontSize: '0.82rem', gap: 1.2, minHeight: 36 }}
                      >
                        <Box sx={{ display: 'flex', color: 'text.secondary', '& > *': { fontSize: 17 } }}>{item.icon}</Box>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>
          )}

          {/* Theme Toggle */}
          <Tooltip title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} arrow>
            <IconButton color="inherit" onClick={handleToggleTheme} size="small" sx={{ mr: 0.5, color: 'rgba(255,255,255,0.85)' }}>
              {themeMode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* User Menu — initials avatar only (saves header space; full name in the
              tooltip + the dropdown). e.g. "Shampa Nandy" → "SN". */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={user?.name || 'User'} arrow>
              <Avatar
                onClick={handleMenuOpen}
                sx={{
                  bgcolor: '#1976D2', color: '#fff', fontWeight: 700,
                  width: 30, height: 30, fontSize: '0.78rem', cursor: 'pointer',
                  border: '1px solid rgba(255,255,255,0.25)',
                  '&:hover': { filter: 'brightness(1.1)' },
                }}
              >
                {(user?.name || 'User').trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || 'U'}
              </Avatar>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <MenuItem onClick={() => { navigate('/settings'); handleMenuClose(); }}>
                <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={() => { handleLogout(); handleMenuClose(); }}>
                <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Round 65 — multi-tab strip (chart tabs opened via "chart icon" call-sites
          via TabsContext.openChartTab). Only renders when >1 tab is open. */}
      <TabStrip />

      {/* Daily Zerodha-login reminder — NSE working days from 08:00 IST; clears ONLY
          on a successful login (status poll) or the per-day Dismiss. Without the daily
          login, option-data capture and SL/Target monitoring idle all session. */}
      {showZerodhaLoginBanner && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0, py: 0.5 }}
          action={
            <Button size="small" color="inherit" onClick={dismissZerodhaBanner} sx={{ fontWeight: 700 }}>
              Dismiss for today
            </Button>
          }
        >
          <strong>Log in to Zerodha for today.</strong> The Kite session expires every morning — without a fresh
          login, NSE/MCX <em>option-data capture</em> and SL/Target monitoring stay paused all day (even if you
          don't plan to trade).
          &nbsp;
          <Button size="small" color="inherit" sx={{ fontWeight: 800, textDecoration: 'underline', p: 0, minWidth: 0 }} onClick={connectZerodhaNow}>
            Login to Zerodha now
          </Button>
        </Alert>
      )}
      {/* Off-window session-expired notice (outside the 08:00–16:00 reminder window,
          e.g. an intraday expiry) — the lighter, per-mount dismissible variant. */}
      {zerodhaWarning && !showZerodhaLoginBanner && !zerodhaWarnDismissed && zBannerDismissedFor !== istToday && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0, py: 0.5 }}
          action={
            <IconButton size="small" color="inherit" onClick={() => setZerodhaWarnDismissed(true)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <strong>Zerodha session expired.</strong> NSE/BSE auto-trading and SL/Target monitoring are paused.
          &nbsp;
          <Button size="small" color="inherit" sx={{ fontWeight: 700, textDecoration: 'underline', p: 0, minWidth: 0 }} onClick={() => navigate('/settings')}>
            Go to Settings to reconnect.
          </Button>
        </Alert>
      )}

      {/* Mobile navigation drawer (< md width) */}
      <Drawer
        anchor="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        PaperProps={{ sx: { width: 260 } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ color: '#7eb6ff' }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>earngush</Typography>
        </Box>
        <Divider />
        <List sx={{ py: 0 }}>
          {menuItems.map((item) => (
            <ListItemButton
              key={item.path}
              selected={isActive(item.path)}
              onClick={() => {
                if ((item as any).newTab) {
                  window.open(item.path, '_blank', 'noopener,noreferrer');
                } else {
                  navigate(item.path);
                }
                setMobileNavOpen(false);
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.path === '/alerts' && unreadAlerts > 0 ? (
                  <Badge color="error" badgeContent={unreadAlerts}>{item.icon}</Badge>
                ) : item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: isActive(item.path) ? 700 : 500 }} />
            </ListItemButton>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <ListItemButton onClick={() => { handleToggleTheme(); }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              {themeMode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={themeMode === 'dark' ? 'Light theme' : 'Dark theme'} />
          </ListItemButton>
          <ListItemButton onClick={() => { handleLogout(); setMobileNavOpen(false); }}>
            <ListItemIcon sx={{ minWidth: 36 }}><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </List>
      </Drawer>

      <CommandPalette />
      <HotkeyOverlay />
      <ManualScanReminderBanner />

      <Container
        maxWidth={location.pathname.startsWith('/charts') ? false : 'xl'}
        disableGutters={location.pathname.startsWith('/charts')}
        sx={{
          mt: location.pathname.startsWith('/charts') ? 0 : 2,
          mb: location.pathname.startsWith('/charts') ? 0 : 2,
          flexGrow: 1,
          minHeight: 0,  // critical: allow flex children to shrink below content height
          px: location.pathname.startsWith('/charts') ? 0 : { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          overflow: location.pathname.startsWith('/charts') ? 'hidden' : undefined,
        }}
      >
        <RouteTransition>
          <Outlet />
        </RouteTransition>
      </Container>
    </Box>
  );
};

export default Layout;
